import type { MarkData } from '../config/types';
import { getFieldValue } from '../tableau/mapping';

const CHIP_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'alert_id', label: 'ID' },
  { key: 'ALERT TYPE', label: 'Type' },
  { key: 'ALERT STATUS', label: 'Status' },
  { key: 'HCP NAME', label: 'HCP' },
];

export interface FormElements {
  container: HTMLDivElement;
  summary: HTMLDivElement;
  dropdown: HTMLSelectElement;
  submitButton: HTMLButtonElement;
}

export interface FormState {
  dropdownValue: string;
  isValid: boolean;
}

export function createForm(dropdownOptions: string[]): FormElements {
  const container = document.createElement('div');
  container.className = 'form-container';

  const summary = document.createElement('div');
  summary.className = 'record-summary empty-state';
  summary.textContent = 'Select a row from the Alerts List worksheet';

  const dropdown = document.createElement('select');
  dropdown.id = 'action-dropdown';
  dropdown.className = 'form-dropdown';
  dropdown.required = true;

  const placeholderOption = document.createElement('option');
  placeholderOption.value = '';
  placeholderOption.textContent = 'Select a dismissal reason...';
  placeholderOption.disabled = true;
  placeholderOption.selected = true;
  dropdown.appendChild(placeholderOption);

  for (const option of dropdownOptions) {
    const optionElement = document.createElement('option');
    optionElement.value = option;
    optionElement.textContent = option;
    dropdown.appendChild(optionElement);
  }

  const submitButton = document.createElement('button');
  submitButton.type = 'button';
  submitButton.className = 'submit-button';
  submitButton.disabled = true;

  const spinner = document.createElement('span');
  spinner.className = 'spinner';
  submitButton.appendChild(spinner);

  const btnLabel = document.createElement('span');
  btnLabel.textContent = 'Dismiss alert';
  submitButton.appendChild(btnLabel);

  container.appendChild(summary);
  container.appendChild(dropdown);
  container.appendChild(submitButton);

  return { container, summary, dropdown, submitButton };
}

export function updateFormWithMark(elements: FormElements, markData: MarkData | null): void {
  if (!markData || Object.keys(markData.fields).length === 0) {
    elements.summary.textContent = 'Select a row from the Alerts List worksheet';
    elements.summary.className = 'record-summary empty-state';
    return;
  }

  elements.summary.className = 'record-summary';
  elements.summary.innerHTML = '';

  const row = document.createElement('div');
  row.className = 'mark-chip-row';

  let chipCount = 0;
  for (const { key, label } of CHIP_FIELDS) {
    const value = getFieldValue(markData, key);
    if (value === null || value === undefined || value === '') continue;

    if (chipCount > 0) {
      const sep = document.createElement('span');
      sep.className = 'mark-chip-sep';
      sep.textContent = '\u00B7';
      row.appendChild(sep);
    }

    const chip = document.createElement('span');
    chip.className = 'mark-chip';

    const chipLabel = document.createElement('span');
    chipLabel.className = 'mark-chip-label';
    chipLabel.textContent = label;

    const chipValue = document.createElement('span');
    chipValue.className = 'mark-chip-value';
    chipValue.textContent = String(value);

    chip.appendChild(chipLabel);
    chip.appendChild(chipValue);
    row.appendChild(chip);
    chipCount++;
  }

  if (chipCount === 0) {
    const fallback = document.createElement('span');
    fallback.className = 'mark-chip-value';
    fallback.textContent = markData.key ?? 'Selected alert';
    row.appendChild(fallback);
  }

  elements.summary.appendChild(row);
}

export function getFormState(elements: FormElements): FormState {
  const dropdownValue = elements.dropdown.value;
  return {
    dropdownValue,
    isValid: dropdownValue !== '',
  };
}

export function resetForm(elements: FormElements): void {
  elements.dropdown.selectedIndex = 0;
}

export function setFormEnabled(elements: FormElements, enabled: boolean): void {
  elements.dropdown.disabled = !enabled;
  elements.submitButton.disabled = !enabled;
}

export function updateSubmitEnabled(
  elements: FormElements,
  hasSelection: boolean,
  formValid: boolean,
  isSubmitting: boolean
): void {
  elements.submitButton.disabled = !hasSelection || !formValid || isSubmitting;
}

export function bindFormEvents(elements: FormElements, onValidationChange: () => void): void {
  elements.dropdown.addEventListener('change', onValidationChange);
}
